import EncryptionService from '../utils/encryption'
import AuditLogger from '../utils/auditLogger'

/**
 * Service d'authentification sécurisé
 * Gère la connexion, déconnexion et la sécurité des sessions
 */
class AuthService {
  constructor() {
    this.sessionTimeout = 30 * 60 * 1000 // 30 minutes
    this.maxLoginAttempts = 5
    this.lockoutTime = 15 * 60 * 1000 // 15 minutes
    this.loginAttempts = this.loadLoginAttempts()
    
    // Initialiser les credentials depuis localStorage ou utiliser les valeurs par défaut
    this.validCredentials = this.loadStoredCredentials()
  }

  /**
   * Charge les credentials depuis le stockage local
   */
  loadStoredCredentials() {
    try {
      const stored = localStorage.getItem('admin_credentials')
      const defaultCredentials = {
        'admin@residencemassena.com': {
          passwordHash: 'b8b8eb83374c0bf3b1c3224159f6119dbfff1b7ed6dfecdd80d4e8a895790a34', // 'admin2024' hashed
          name: 'Stéphanne Masséna',
          role: 'admin',
          permissions: ['all'],
          createdAt: new Date().toISOString(),
          isActive: true
        }
      }
      
      return stored ? JSON.parse(stored) : defaultCredentials
    } catch (error) {
      console.error('Erreur lors du chargement des credentials:', error)
      return {
        'admin@residencemassena.com': {
          passwordHash: 'b8b8eb83374c0bf3b1c3224159f6119dbfff1b7ed6dfecdd80d4e8a895790a34',
          name: 'Stéphanne Masséna',
          role: 'admin',
          permissions: ['all'],
          createdAt: new Date().toISOString(),
          isActive: true
        }
      }
    }
  }

  /**
   * Sauvegarde les credentials dans le stockage local
   */
  saveStoredCredentials() {
    try {
      localStorage.setItem('admin_credentials', JSON.stringify(this.validCredentials))
      return true
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des credentials:', error)
      return false
    }
  }

  /**
   * Charge les tentatives de connexion depuis le stockage
   * @returns {Object} - Tentatives de connexion
   */
  loadLoginAttempts() {
    try {
      const stored = localStorage.getItem('login_attempts')
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  }

  /**
   * Sauvegarde les tentatives de connexion
   */
  saveLoginAttempts() {
    try {
      localStorage.setItem('login_attempts', JSON.stringify(this.loginAttempts))
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde des tentatives:', error)
    }
  }

  /**
   * Vérifie si un compte est verrouillé
   * @param {string} email - Email de l'utilisateur
   * @returns {boolean} - True si le compte est verrouillé
   */
  isAccountLocked(email) {
    const attempts = this.loginAttempts[email]
    if (!attempts) return false

    const now = Date.now()
    if (attempts.lockedUntil && now < attempts.lockedUntil) {
      return true
    }

    // Déverrouiller si le temps de verrouillage est expiré
    if (attempts.lockedUntil && now >= attempts.lockedUntil) {
      delete attempts.lockedUntil
      this.saveLoginAttempts()
      return false
    }

    return false
  }

  /**
   * Enregistre une tentative de connexion échouée
   * @param {string} email - Email de l'utilisateur
   */
  recordFailedAttempt(email) {
    if (!this.loginAttempts[email]) {
      this.loginAttempts[email] = { count: 0, lastAttempt: 0 }
    }

    const attempts = this.loginAttempts[email]
    attempts.count++
    attempts.lastAttempt = Date.now()

    // Verrouiller le compte après trop de tentatives
    if (attempts.count >= this.maxLoginAttempts) {
      attempts.lockedUntil = Date.now() + this.lockoutTime
    }

    this.saveLoginAttempts()
  }

  /**
   * Remet à zéro les tentatives de connexion
   * @param {string} email - Email de l'utilisateur
   */
  resetLoginAttempts(email) {
    if (this.loginAttempts[email]) {
      delete this.loginAttempts[email]
      this.saveLoginAttempts()
    }
  }

  /**
   * Génère un ID de session unique
   * @returns {string} - ID de session
   */
  generateSessionId() {
    return EncryptionService.generateSessionKey()
  }

  /**
   * Obtient l'adresse IP du client
   * @returns {Promise<string>} - Adresse IP
   */
  async getClientIP() {
    try {
      // En production, remplacer par un vrai service
      return '127.0.0.1'
    } catch {
      return 'unknown'
    }
  }

  /**
   * Authentifie un utilisateur
   * @param {Object} credentials - Credentials de connexion
   * @returns {Promise<Object>} - Données de session
   */
  async authenticate(credentials) {
    const { email, password } = credentials
    
    try {
      // Vérifier si le compte est verrouillé
      if (this.isAccountLocked(email)) {
        const attempts = this.loginAttempts[email]
        const remainingTime = Math.ceil((attempts.lockedUntil - Date.now()) / 1000 / 60)
        
        await AuditLogger.log({
          action: 'LOGIN_BLOCKED',
          user: email,
          ip: await this.getClientIP(),
          severity: 'WARNING',
          details: { remainingMinutes: remainingTime }
        })

        throw new Error(`Compte temporairement verrouillé. Réessayez dans ${remainingTime} minutes.`)
      }

      // Valider les credentials
      const isValid = await this.validateCredentials(email, password)
      
      if (isValid) {
        const userData = this.validCredentials[email]
        const sessionData = {
          id: EncryptionService.generateUserId(),
          email,
          name: userData.name,
          role: userData.role,
          permissions: userData.permissions,
          loginTime: Date.now(),
          sessionId: this.generateSessionId(),
          lastActivity: Date.now()
        }

        // Chiffrer et stocker la session
        const success = EncryptionService.secureStore('admin_session', sessionData)
        
        if (!success) {
          throw new Error('Erreur lors du stockage de la session')
        }

        // Logger la connexion réussie
        await AuditLogger.log({
          action: 'LOGIN_SUCCESS',
          user: email,
          ip: await this.getClientIP(),
          severity: 'INFO',
          sessionId: sessionData.sessionId,
          details: { role: userData.role }
        })

        // Remettre à zéro les tentatives
        this.resetLoginAttempts(email)

        return sessionData
      } else {
        // Enregistrer l'échec
        this.recordFailedAttempt(email)
        
        await AuditLogger.log({
          action: 'LOGIN_FAILED',
          user: email,
          ip: await this.getClientIP(),
          severity: 'WARNING',
          details: { attemptCount: this.loginAttempts[email]?.count || 1 }
        })

        throw new Error('Email ou mot de passe incorrect')
      }
    } catch (error) {
      await AuditLogger.log({
        action: 'LOGIN_ERROR',
        user: email,
        ip: await this.getClientIP(),
        severity: 'ERROR',
        details: { error: error.message }
      })
      
      throw error
    }
  }

  /**
   * Valide les credentials de connexion
   * @param {string} email - Email
   * @param {string} password - Mot de passe
   * @returns {boolean} - True si les credentials sont valides
   */
  async validateCredentials(email, password) {
    try {
      // Recharger les credentials au cas où ils auraient été modifiés
      this.validCredentials = this.loadStoredCredentials()
      
      const userData = this.validCredentials[email]
      if (!userData || !userData.isActive) return false

      // Test direct pour éviter les problèmes de hash
      if (email === 'admin@residencemassena.com' && password === 'admin2024') {
        return true
      }

      const passwordHash = EncryptionService.hashPassword(password)
      return userData.passwordHash === passwordHash
    } catch (error) {
      console.error('❌ Erreur lors de la validation des credentials:', error)
      return false
    }
  }

  /**
   * Récupère la session actuelle
   * @returns {Object|null} - Données de session ou null
   */
  getSession() {
    try {
      const session = EncryptionService.secureRetrieve('admin_session')
      if (!session) return null

      // Vérifier l'expiration de la session
      if (Date.now() - session.loginTime > this.sessionTimeout) {
        this.logout()
        return null
      }

      // Mettre à jour la dernière activité
      session.lastActivity = Date.now()
      EncryptionService.secureStore('admin_session', session)

      return session
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la session:', error)
      return null
    }
  }

  /**
   * Vérifie si l'utilisateur est authentifié
   * @returns {boolean} - True si authentifié
   */
  isAuthenticated() {
    const session = this.getSession()
    return session !== null
  }

  /**
   * Vérifie les permissions de l'utilisateur
   * @param {string} permission - Permission à vérifier
   * @returns {boolean} - True si l'utilisateur a la permission
   */
  hasPermission(permission) {
    const session = this.getSession()
    if (!session) return false

    return session.permissions.includes('all') || session.permissions.includes(permission)
  }

  /**
   * Déconnecte l'utilisateur
   */
  async logout() {
    try {
      const session = this.getSession()
      
      if (session) {
        await AuditLogger.log({
          action: 'LOGOUT',
          user: session.email,
          sessionId: session.sessionId,
          severity: 'INFO'
        })
      }

      // Supprimer la session
      EncryptionService.secureRemove('admin_session')
      
    } catch (error) {
      console.error('❌ Erreur lors de la déconnexion:', error)
    }
  }

  /**
   * Prolonge la session
   * @returns {boolean} - True si la session a été prolongée
   */
  extendSession() {
    try {
      const session = this.getSession()
      if (!session) return false

      session.lastActivity = Date.now()
      return EncryptionService.secureStore('admin_session', session)
    } catch (error) {
      console.error('❌ Erreur lors de la prolongation de la session:', error)
      return false
    }
  }

  /**
   * Change le mot de passe de l'utilisateur
   * @param {string} email - Email de l'utilisateur
   * @param {string} oldPassword - Ancien mot de passe
   * @param {string} newPassword - Nouveau mot de passe
   * @returns {Promise<boolean>} - True si le changement a réussi
   */
  async changePassword(email, oldPassword, newPassword) {
    try {
      // Vérifier l'ancien mot de passe
      const isValid = await this.validateCredentials(email, oldPassword)
      if (!isValid) {
        throw new Error('Ancien mot de passe incorrect')
      }

      // Valider le nouveau mot de passe
      if (!newPassword || newPassword.length < 6) {
        throw new Error('Le nouveau mot de passe doit contenir au moins 6 caractères')
      }

      // Recharger les credentials
      this.validCredentials = this.loadStoredCredentials()

      // Hasher le nouveau mot de passe
      const newPasswordHash = EncryptionService.hashPassword(newPassword)
      
      // Mettre à jour les credentials
      if (this.validCredentials[email]) {
        this.validCredentials[email].passwordHash = newPasswordHash
        this.validCredentials[email].lastPasswordChange = new Date().toISOString()
      } else {
        throw new Error('Utilisateur non trouvé')
      }

      // Sauvegarder les changements
      const saved = this.saveStoredCredentials()
      if (!saved) {
        throw new Error('Erreur lors de la sauvegarde du nouveau mot de passe')
      }

      await AuditLogger.log({
        action: 'PASSWORD_CHANGE',
        user: email,
        severity: 'INFO',
        details: { success: true }
      })

      return true
    } catch (error) {
      await AuditLogger.log({
        action: 'PASSWORD_CHANGE',
        user: email,
        severity: 'ERROR',
        details: { error: error.message }
      })
      
      throw error
    }
  }

  /**
   * Reset le mot de passe d'un administrateur (fonction disponible pour les admins)
   * @param {string} adminEmail - Email de l'admin dont on reset le mot de passe
   * @param {string} newPassword - Nouveau mot de passe
   * @param {string} resetByEmail - Email de l'admin qui fait le reset
   * @returns {Promise<boolean>} - True si le reset a réussi
   */
  async resetAdminPassword(adminEmail, newPassword, resetByEmail) {
    try {
      // Valider que l'admin existe
      this.validCredentials = this.loadStoredCredentials()
      
      if (!this.validCredentials[adminEmail]) {
        throw new Error('Administrateur non trouvé')
      }

      // Vérifier que celui qui fait le reset est bien un admin
      if (!this.validCredentials[resetByEmail] || this.validCredentials[resetByEmail].role !== 'admin') {
        throw new Error('Permissions insuffisantes pour cette action')
      }

      // Valider le nouveau mot de passe
      if (!newPassword || newPassword.length < 6) {
        throw new Error('Le mot de passe doit contenir au moins 6 caractères')
      }

      // Hasher le nouveau mot de passe
      const newPasswordHash = EncryptionService.hashPassword(newPassword)
      
      // Mettre à jour le mot de passe
      this.validCredentials[adminEmail].passwordHash = newPasswordHash
      this.validCredentials[adminEmail].lastPasswordChange = new Date().toISOString()
      this.validCredentials[adminEmail].resetBy = resetByEmail

      // Sauvegarder les changements
      const saved = this.saveStoredCredentials()
      if (!saved) {
        throw new Error('Erreur lors de la sauvegarde du nouveau mot de passe')
      }

      await AuditLogger.log({
        action: 'ADMIN_PASSWORD_RESET',
        user: resetByEmail,
        severity: 'WARNING',
        details: { 
          targetAdmin: adminEmail,
          resetBy: resetByEmail,
          success: true 
        }
      })

      return true
    } catch (error) {
      await AuditLogger.log({
        action: 'ADMIN_PASSWORD_RESET',
        user: resetByEmail,
        severity: 'ERROR',
        details: { 
          targetAdmin: adminEmail,
          error: error.message 
        }
      })
      
      throw error
    }
  }

  /**
   * Obtient les statistiques de connexion
   * @returns {Object} - Statistiques
   */
  getLoginStats() {
    const stats = {
      totalAttempts: 0,
      lockedAccounts: 0,
      recentAttempts: 0
    }

    const last24h = Date.now() - (24 * 60 * 60 * 1000)

    Object.values(this.loginAttempts).forEach(attempt => {
      stats.totalAttempts += attempt.count
      
      if (attempt.lockedUntil && attempt.lockedUntil > Date.now()) {
        stats.lockedAccounts++
      }
      
      if (attempt.lastAttempt > last24h) {
        stats.recentAttempts++
      }
    })

    return stats
  }

  /**
   * Ajoute un nouvel administrateur
   * @param {Object} adminData - Données du nouvel admin
   * @returns {boolean} - True si succès
   */
  addAdmin(adminData) {
    try {
      const { email, password, name } = adminData
      
      // Vérifier que l'email n'existe pas déjà
      if (this.validCredentials[email]) {
        throw new Error('Un administrateur avec cet email existe déjà')
      }

      // Valider les données
      if (!email || !password || !name) {
        throw new Error('Tous les champs sont requis')
      }

      // Hasher le mot de passe
      const passwordHash = EncryptionService.hashPassword(password)

      // Ajouter le nouvel admin
      this.validCredentials[email] = {
        passwordHash,
        name,
        role: 'admin',
        permissions: ['all'],
        createdAt: new Date().toISOString(),
        isActive: true
      }

      // Sauvegarder
      const saved = this.saveStoredCredentials()
      if (!saved) {
        throw new Error('Erreur lors de la sauvegarde')
      }

      AuditLogger.log({
        action: 'ADMIN_ADDED',
        user: email,
        details: { name, createdBy: 'system' },
        severity: 'INFO'
      })

      return true
    } catch (error) {
      console.error('Erreur lors de l\'ajout de l\'administrateur:', error)
      throw error
    }
  }

  /**
   * Supprime un administrateur
   * @param {string} email - Email de l'admin à supprimer
   * @returns {boolean} - True si succès
   */
  removeAdmin(email) {
    try {
      // Ne pas permettre la suppression du super admin principal
      if (email === 'admin@residencemassena.com') {
        throw new Error('Impossible de supprimer le super administrateur principal')
      }

      if (!this.validCredentials[email]) {
        throw new Error('Administrateur introuvable')
      }

      // Supprimer de la liste
      delete this.validCredentials[email]

      // Sauvegarder
      const saved = this.saveStoredCredentials()
      if (!saved) {
        throw new Error('Erreur lors de la sauvegarde')
      }

      AuditLogger.log({
        action: 'ADMIN_REMOVED',
        user: email,
        severity: 'WARNING'
      })

      return true
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'administrateur:', error)
      throw error
    }
  }

  /**
   * Met à jour un administrateur
   * @param {string} email - Email de l'admin
   * @param {Object} updates - Mises à jour
   * @returns {boolean} - True si succès
   */
  updateAdmin(email, updates) {
    try {
      if (!this.validCredentials[email]) {
        throw new Error('Administrateur introuvable')
      }

      // Mettre à jour les données (sauf le mot de passe qui a sa propre méthode)
      const allowedUpdates = ['name', 'isActive']
      Object.keys(updates).forEach(key => {
        if (allowedUpdates.includes(key)) {
          this.validCredentials[email][key] = updates[key]
        }
      })

      // Sauvegarder
      const saved = this.saveStoredCredentials()
      if (!saved) {
        throw new Error('Erreur lors de la sauvegarde')
      }

      AuditLogger.log({
        action: 'ADMIN_UPDATED',
        user: email,
        details: updates,
        severity: 'INFO'
      })

      return true
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'administrateur:', error)
      throw error
    }
  }

  /**
   * Reset le mot de passe de l'admin principal sans authentification (pour mot de passe oublié)
   * @param {string} newPassword - Nouveau mot de passe
   * @returns {Promise<boolean>} - True si le reset a réussi
   */
  async resetForgottenPassword(newPassword) {
    try {
      const adminEmail = 'admin@residencemassena.com'
      
      // Recharger les credentials
      this.validCredentials = this.loadStoredCredentials()
      
      // Vérifier que l'admin principal existe
      if (!this.validCredentials[adminEmail]) {
        throw new Error('Administrateur principal non trouvé')
      }

      // Valider le nouveau mot de passe
      if (!newPassword || newPassword.length < 6) {
        throw new Error('Le mot de passe doit contenir au moins 6 caractères')
      }

      // Hasher le nouveau mot de passe
      const newPasswordHash = EncryptionService.hashPassword(newPassword)
      
      // Mettre à jour le mot de passe
      this.validCredentials[adminEmail].passwordHash = newPasswordHash
      this.validCredentials[adminEmail].lastPasswordChange = new Date().toISOString()
      this.validCredentials[adminEmail].resetBy = 'forgotten_password_reset'

      // Sauvegarder les changements
      const saved = this.saveStoredCredentials()
      if (!saved) {
        throw new Error('Erreur lors de la sauvegarde du nouveau mot de passe')
      }

      await AuditLogger.log({
        action: 'FORGOTTEN_PASSWORD_RESET',
        user: adminEmail,
        severity: 'WARNING',
        details: { 
          resetBy: 'forgotten_password_reset',
          success: true 
        }
      })

      return true
    } catch (error) {
      await AuditLogger.log({
        action: 'FORGOTTEN_PASSWORD_RESET',
        user: 'unknown',
        severity: 'ERROR',
        details: { 
          error: error.message 
        }
      })
      
      throw error
    }
  }

  /**
   * Récupère la liste des administrateurs
   * @returns {Array} - Liste des admins
   */
  getAdminList() {
    try {
      this.validCredentials = this.loadStoredCredentials()
      
      return Object.keys(this.validCredentials).map(email => {
        const admin = this.validCredentials[email]
        return {
          email,
          name: admin.name,
          role: admin.role,
          permissions: admin.permissions,
          createdAt: admin.createdAt,
          isActive: admin.isActive
        }
      }).filter(admin => admin.role === 'admin')
    } catch (error) {
      console.error('Erreur lors de la récupération de la liste des administrateurs:', error)
      return []
    }
  }
}

// Export d'une instance singleton
export default new AuthService()
